import React, { useState } from 'react';

import { LoadingButton, Stack, StringHelper } from '@asicsdigital/oneasics-blocks';
import { yupResolver } from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useTracking } from 'react-tracking';
import * as yup from 'yup';


import { FORM_INPUT_SPACING, handleFormErrorAnalytics, StyledFormError } from './shared';
import { useCopyWithLinks } from '../../hooks/use-copy-with-links';
import { useCountryOverrides } from '../../hooks/use-country-overrides';
import { oauthApi } from '../../services/api';
import { setIsLoggedIn } from '../../store/userSlice';
import Spinner from '../spinner/Spinner';
import UserInfoInputs from '../user-info-inputs/UserInfoInputs';

/**
 * A basic email and password login form that includes form validation.
 */
const LoginForm = ({ children = null }) => {
  const dispatch = useDispatch();
  const {
    client: { clientId },
    queryParams: { autoOptIn, style, utmCampaign },
    user: { email },
    ui: { emailFromLander },
  } = useSelector((state) => state);
  const [parsedError, setParsedError] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const { trackEvent } = useTracking();
  const { i18n, t } = useTranslation();

  const copyWithLinks = useCopyWithLinks({ copyKeys: [parsedError] });

  const formCopy = useCountryOverrides('login', style);

  const [login] = oauthApi.useLoginMutation();

  const schema = yup.object().shape({
    email: yup
      .string()
      .required(t('errors:missingRequiredField')),
    password: yup
      .string()
      .required(t('errors:missingLoginPassword')),
  });

  const { control, register, handleSubmit, trigger, formState: { errors } } = useForm({
    defaultValues: {
      email: emailFromLander || email,
    },
    resolver: yupResolver(schema),
  });

  i18n.on('languageChanged', () => {
    trigger(Object.keys(errors));
  });

  const onFormError = (formErrors) => {
    setIsLoading(false);
    handleFormErrorAnalytics({ formErrors, trackEvent });
  };

  const onFormSubmit = (formData) => {
    setIsLoading(true);

    login({
      email: formData.email,
      password: formData.password,
      grant_type: 'password',
      client_id: clientId || 'idm',
      receive_newsletter: autoOptIn === 'true',
      response_type: 'other',
      tealium_visitor_id: window.utag_data?.tealium_visitor_id,
      utm_campaign: utmCampaign || '',
    }).unwrap().then((response) => {
      const user = response?.user;
      dispatch(setIsLoggedIn({
        asicsId: user.asicsId,
        csrfToken: response?.csrfToken,
      }));
      trackEvent({ event_type: 'success', asics_id: user.asicsId });
    }).catch((error) => {
      setIsLoading(false);
      const errorMessage = error?.data?.reasons?.[0] || 'internal_server';
      setParsedError(StringHelper.toCamelCase(errorMessage));
      trackEvent({ event_type: 'error', error: errorMessage });
    });
  };

  return (
    <Stack noValidate as="form" spacing={FORM_INPUT_SPACING} onSubmit={handleSubmit(onFormSubmit, onFormError)}>
      {
        parsedError && (
          <StyledFormError id={parsedError}>
            {copyWithLinks[parsedError]}
          </StyledFormError>
        )
      }

      <UserInfoInputs
        emailAddressShown
        passwordVisible
        birthdayVisible={false}
        control={control}
        disableInputs={isLoading}
        emailVisible={false}
        errors={errors}
        formCopy={formCopy}
        register={register}
      />

      {children}

      <LoadingButton id="login" isLoading={isLoading} name="login" spinner= {Spinner} type="submit">
        {t('login.submit')}
      </LoadingButton>
    </Stack>
  );
};

LoginForm.propTypes = {
  /**
   * Extra space to put in things like "Forgot Password" links.
   */
  children: PropTypes.node,
};

export default LoginForm;
